import * as React from "react";
import { v4 as uuid } from "uuid";

export interface WinSpedPreviewViewerProps {
    output: string;
}

// ---- Format detection ----

export function parseWinSpedData(output: string): boolean {
    if (!output) return false;
    const firstLine = output.trim().split(/\r?\n/)[0] ?? "";
    return firstLine.startsWith("START|");
}

// ---- Value formatting (same logic as ExportSimLisDocTable) ----

function fmtVal(value: any): string {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "boolean") return value ? "J" : "N";
    if (typeof value === "object") {
        if (value.year !== undefined) {
            return `${String(value.day).padStart(2,"0")}.${String(value.month).padStart(2,"0")}.${value.year}`;
        }
        if (Array.isArray(value) && value.length > 0) return fmtVal(value[0]);
    }
    // ISO date (LocalDate is serialized as e.g. "2026-08-28")
    if (typeof value === "string") {
        const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    }
    return String(value);
}

function fmtTime(t: any): string {
    if (!t) return "";
    const digits = String(t).trim().replace(/[^0-9]/g, "");
    // "17:00:00" -> "17:00", "17:00" -> "17:00", "1700" -> "17:00", "7:00" -> "07:00"
    if (digits.length >= 4) return `${digits.slice(0,2)}:${digits.slice(2,4)}`;
    if (digits.length === 3) return `0${digits.slice(0,1)}:${digits.slice(1,3)}`;
    return String(t).trim();
}

function fmtDateObj(d: any): string {
    if (!d) return "";
    if (typeof d === "object" && d.year !== undefined) {
        return `${String(d.day).padStart(2,"0")}.${String(d.month).padStart(2,"0")}.${d.year}`;
    }
    // ISO date (LocalDate is serialized as e.g. "2026-08-28", DateTime contains a date prefix as well)
    const s = String(d).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return s;
}

// ---- Address type mapping ----

const ADR_LABELS: Record<string|number, string> = {
    0: "Abs.", 1: "Empf.", 2: "Lieferadr.", 3: "Rg.-Empf.",
    4: "Kun", 5: "Frz",
    10: "Ladeadr. n.", 11: "Entladeadr. n.", 12: "Rg.-Empf.",
    16: "Endempf.", 26: "Abw. Abs.", 27: "Abw. Empf.",
    // string variants
    "Absender": "Abs.", "Empfänger": "Empf.", "Auftraggeber": "Kun",
    "Frachtzahler": "Frz", "Endempfänger": "Endempf.",
};

const TEXT_LABELS: Record<string|number, string> = {
    101: "Rechnung", 102: "Gutschrift", 103: "Anlieferung",
    104: "Sped.-Auftrag", 105: "Erklärungen/Vereinbarungen", 106: "Disposition",
    107: "Auftragsetikett", 108: "Tourbemerkung 1", 109: "Tourbemerkung 2",
    110: "kundenspezifisch, bitte nur nach Abstimmung nutzen: Storno", 111: "SL: Auftragsinfo LD: Informationen", 112: "Tourkennung",
    201: "Sendungsinfo: Absender", 202: "Sendungsinfo: Empfänger",
    203: "Sendungsinfo: Frachtzahler", 204: "Sendungsinfo: Frachtführer",
    205: "Sendungsinfo: Urabsender",
};

function textLabel(art: any): string {
    const code = Number(art);
    if (code && TEXT_LABELS[code]) return TEXT_LABELS[code];
    return "";
}

// ---- Styles ----

const teal  = "#006060";
const green = "#00703c";

const S: Record<string, React.CSSProperties> = {
    root: {
        fontFamily: "Segoe UI, Tahoma, Arial, sans-serif",
        fontSize: 11,
        background: "#e0e0e0",
        padding: 6,
        overflowY: "auto",
        height: "100%",
        boxSizing: "border-box",
    },
    header: {
        background: teal,
        color: "#fff",
        fontWeight: "bold",
        padding: "3px 8px",
        fontSize: 12,
        display: "flex",
        gap: 12,
        alignItems: "center",
        marginBottom: 4,
    },
    panel: {
        background: "#f4f4f4",
        border: "1px solid #b0b0b0",
        padding: 6,
        marginBottom: 6,
    },
    sectionTitle: {
        background: "#d0e8d0",
        border: "1px solid #90b890",
        padding: "1px 6px",
        fontWeight: "bold",
        fontSize: 10,
        color: "#005000",
        margin: "4px 0 2px",
    },
    row: {
        display: "flex",
        flexWrap: "wrap" as const,
        alignItems: "center",
        gap: 4,
        marginBottom: 2,
    },
    lbl: { fontSize: 10, color: "#444", whiteSpace: "nowrap" as const },
    lblBold: { fontSize: 10, fontWeight: "bold", color: "#222", whiteSpace: "nowrap" as const },
    inpFix: {
        border: "1px solid #aaa",
        background: "#fff3c0",
        padding: "1px 4px",
        minHeight: 17,
        fontSize: 11,
        fontWeight: "bold",
        display: "inline-flex",
        alignItems: "center",
        whiteSpace: "nowrap" as const,
    },
    th: {
        border: "1px solid #888",
        background: teal,
        color: "#fff",
        padding: "1px 5px",
        fontWeight: "normal",
        fontSize: 10,
        whiteSpace: "nowrap" as const,
        textAlign: "left" as const,
    },
    td: { border: "1px solid #ccc", padding: "1px 5px", fontSize: 11, whiteSpace: "nowrap" as const },
    tdWrap: { border: "1px solid #ccc", padding: "1px 5px", fontSize: 11,
        whiteSpace: "pre-wrap" as const, maxWidth: 250 },
    divider: { borderTop: "1px solid #bbb", margin: "3px 0" },
};

function inp(w?: number, hi?: boolean): React.CSSProperties {
    return {
        border: "1px solid #aaa",
        background: hi ? "#e8f8e8" : "#fff",
        padding: "1px 4px",
        minHeight: 17,
        fontSize: 11,
        display: "inline-flex",
        alignItems: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        minWidth: w ?? 60,
        width: w ?? undefined,
    };
}

/**
 * Builds the loading-equipment texts from LADEMI records.
 * LMArt == 1 -> goes into the "Pal" column, LMArt == 2 -> into the "VPE" column,
 * each as e.g. "1xLDG" (PALAnz x PAL).
 */
function formatLademittel(lademiList: any[]): { vpe: string; pal: string } {
    const vpeParts: string[] = []
    const palParts: string[] = []
    lademiList.forEach(ldm => {
        const art = Number(ldm.map?.LMArt)
        const anz = fmtVal(ldm.map?.PALAnz)
        const lm  = fmtVal(ldm.map?.PAL)
        if (!lm) return
        const text = anz ? `${anz}x${lm}` : lm
        if (art === 1) palParts.push(text)
        else if (art === 2) vpeParts.push(text)
    })
    return { vpe: vpeParts.join(", "), pal: palParts.join(", ") }
}

function TextArtenPopup() {
    const [open, setOpen] = React.useState(false);
    return (
        <span style={{ position: "relative", display: "inline-flex", marginLeft: 4, marginTop: -7, cursor: "help" }}
            onClick={e => e.stopPropagation()}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}>
            <span style={{ fontSize: 10, color: teal, border: "1px solid teal", borderRadius: "50%",
                width: 12, height: 12, display: "inline-flex", alignItems: "center", justifyContent: "center",
                lineHeight: 1, paddingBottom: 1 }}>i</span>
            {open && (
                <div style={{
                    position: "absolute", zIndex: 1000, left: 0, top: 16, minWidth: 300,
                    background: "#fff", border: "1px solid #999", boxShadow: "2px 2px 8px rgba(0,0,0,0.25)",
                    padding: 6, maxHeight: 320, overflowY: "auto",
                }}>
                    <div style={{ fontWeight: "bold", fontSize: 10, color: teal, marginBottom: 4 }}>
                        Text-Arten (TxtArtNr)
                    </div>
                    <table style={{ borderCollapse: "collapse", fontSize: 10 }}>
                        <thead>
                            <tr>
                                <th style={S.th}>Nr.</th>
                                <th style={S.th}>Bezeichnung</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(TEXT_LABELS)
                                .sort((a, b) => Number(a[0]) - Number(b[0]))
                                .map(([code, name]) => (
                                    <tr key={code}>
                                        <td style={{ ...S.td, textAlign: "right" }}>{code}</td>
                                        <td style={S.td}>{name}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            )}
        </span>
    );
}

function adrBadge(art: string|number): React.CSSProperties {
    return {
        background: art == 4 || art === "Auftraggeber" ? teal
                  : art == 5 || art === "Frachtzahler" ? "#8b4513"
                  : art == 0 || art === "Absender" ? green
                  : art == 1 || art === "Empfänger" ? "#1a3a8c"
                  : "#666",
        color: "#fff",
        fontWeight: "bold",
        fontSize: 10,
        padding: "1px 5px",
        whiteSpace: "nowrap",
        flexShrink: 0,
        alignSelf: "center",
    };
}

function Lbl({ t, bold }: { t: string; bold?: boolean }) {
    return <span style={bold ? S.lblBold : S.lbl}>{t}</span>;
}

function In({ v, w, hi }: { v?: any; w?: number; hi?: boolean }) {
    const s = fmtVal(v);
    if (!s) return null;
    return <div style={inp(w, hi)}>{s}</div>;
}

function AdrRow({ rec }: { rec: any }) {
    const m = rec.map;
    const art = m.KunArt ?? m.ArtNr ?? "";
    const label = ADR_LABELS[art] ?? String(art);
    const name = fmtVal(m.Name1);
    const nr   = fmtVal(m.KundenNr);
    if (!name && !nr) return null;
    return (
        <div style={S.row}>
            <div style={adrBadge(art)}>{label}</div>
            {nr   && <div style={inp(70)}>{nr}</div>}
            {name && <div style={{ ...inp(), minWidth: 160, fontWeight: "bold" }}>{name}</div>}
            {fmtVal(m.Name2) && <div style={inp(120)}>{fmtVal(m.Name2)}</div>}
            {fmtVal(m.Strasse) && <div style={inp(140)}>{fmtVal(m.Strasse)}</div>}
            {fmtVal(m.LKZ) && <div style={inp(24)}>{fmtVal(m.LKZ)}</div>}
            {fmtVal(m.PLZ) && <div style={inp(50)}>{fmtVal(m.PLZ)}</div>}
            {fmtVal(m.Ort) && <div style={{ ...inp(), background: "#d4f0d4", fontWeight: "bold", minWidth: 100 }}>{fmtVal(m.Ort)}</div>}
        </div>
    );
}

function DateRow({ label, vonDat, vonZeit, bisDat, bisZeit, fixDat, fixZeit, fixBDat, fixBZeit }: {
    label: string;
    vonDat?: any; vonZeit?: any; bisDat?: any; bisZeit?: any;
    fixDat?: any; fixZeit?: any; fixBDat?: any; fixBZeit?: any;
}) {
    const hasFix = fixDat || fixZeit || fixBDat || fixBZeit;
    return (
        <div style={S.row}>
            <Lbl t={`${label}:`} bold />
            {vonDat  && <><Lbl t="von" /><div style={inp(72)}>{fmtDateObj(vonDat)}</div></>}
            {vonZeit && <div style={inp(40)}>{fmtTime(vonZeit)}</div>}
            {bisDat  && <><Lbl t="bis" /><div style={inp(72)}>{fmtDateObj(bisDat)}</div></>}
            {bisZeit && <div style={inp(40)}>{fmtTime(bisZeit)}</div>}
            {hasFix && (
                <>
                    <div style={S.inpFix}>Fix</div>
                    {fixDat  && <div style={inp(72)}>{fmtDateObj(fixDat)}</div>}
                    {fixZeit && <div style={inp(40)}>{fmtTime(fixZeit)}</div>}
                    {fixBDat && <><Lbl t="-" /><div style={inp(72)}>{fmtDateObj(fixBDat)}</div></>}
                    {fixBZeit && <div style={inp(40)}>{fmtTime(fixBZeit)}</div>}
                </>
            )}
        </div>
    );
}

function OrderView({ records }: { records: any[] }) {
    const byType = (type: string) => records.filter(r => r.map?.Satzart === type);

    const auftrList = byType("AUFTR");
    const adrList   = byType("ADR");
    const positList = byType("POSIT");
    const lademiList = byType("LADEMI");
    const textList  = byType("TEXT");
    const ladeliste = byType("LADELI");
    const nveList   = byType("NVE");
    const statusList = byType("STATUS");

    const [textsOpen, setTextsOpen] = React.useState(false);
    const [nveOpen,   setNveOpen]   = React.useState(false);

    return (
        <>
            {auftrList.map((auftr, ai) => {
                const m = auftr.map;
                const aufNr = fmtVal(m.AufNr ?? m.TransNr);
                const liefNr = fmtVal(m.LiefNr);
                const refNr  = fmtVal(m.RefNr);
                const kommNr = fmtVal(m.KommNr);

                // Address grouping
                const adrForAuftrag = adrList.filter(a => {
                    const aufNrA = fmtVal(a.map?.AufNr ?? a.map?.TransNr ?? "");
                    return aufNrA === aufNr || aufNrA === "";
                });

                // ADR display order: 4(Kun), 0(Abs/BSt), 1(Empf/ESt), 5(Frz), rest
                const sortedAdr = [...adrForAuftrag].sort((a, b) => {
                    const order = [4,0,1,5,2,3,10,11,12,16,26,27];
                    const ia = order.indexOf(Number(a.map?.KunArt ?? a.map?.ArtNr ?? 99));
                    const ib = order.indexOf(Number(b.map?.KunArt ?? b.map?.ArtNr ?? 99));
                    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
                });

                return (
                    <div key={ai} style={S.panel}>
                        {/* Order header */}
                        <div style={S.header}>
                            <span>SA-Auftrag</span>
                            {aufNr  && <span>{aufNr}</span>}
                            {liefNr && <span style={{ fontSize: 10, opacity: 0.9 }}>Liefer-Nr.: {liefNr}</span>}
                            {kommNr && <span style={{ fontSize: 10, opacity: 0.9 }}>Komm-Nr.: {kommNr}</span>}
                            {refNr  && <span style={{ fontSize: 10, opacity: 0.9 }}>Ref.: {refNr}</span>}
                            {fmtVal(m.LiefTxt) && <span style={{ fontSize: 10, opacity: 0.9 }}>Terminvermerk: {fmtVal(m.LiefTxt)}</span>}
                            {fmtVal(m.AufKlasse) && <span style={{ fontSize: 10, opacity: 0.85 }}>Klasse: {fmtVal(m.AufKlasse)}</span>}
                            {fmtVal(m.VerkArt) && <span style={{ fontSize: 10, opacity: 0.85 }}>Verk.-Art: {fmtVal(m.VerkArt)}</span>}
                            {fmtVal(m.Tmittel)   && <span style={{ fontSize: 10, opacity: 0.85 }}>Tmittel: {fmtVal(m.Tmittel)}</span>}
                        </div>

                        {/* Addresses */}
                        {sortedAdr.filter(a => {
                            // skip pure-coord records (no name and no customer nr)
                            return fmtVal(a.map?.Name1) || fmtVal(a.map?.KundenNr);
                        }).map((a, i) => <AdrRow key={i} rec={a} />)}

                        <div style={S.divider} />

                        {/* Dates */}
                        <DateRow
                            label="Belade"
                            vonDat={m.BelVonDat}  vonZeit={m.BelVonZeit}
                            bisDat={m.BelBisDat}  bisZeit={m.BelBisZeit}
                            fixDat={m.BelFixDat}  fixZeit={m.BelFixZeit}
                            fixBDat={m.BelFixBD}  fixBZeit={m.BelFixBZ}
                        />
                        <DateRow
                            label="Entlade"
                            vonDat={m.EntVonDat}  vonZeit={m.EntVonZeit}
                            bisDat={m.EntBisDat}  bisZeit={m.EntBisZeit}
                            fixDat={m.EntFixDat}  fixZeit={m.EntFixZeit}
                            fixBDat={m.EntFixBD}  fixBZeit={m.EntFixBZ}
                        />

                        {/* References */}
                        {(fmtVal(m.RefNr) || fmtVal(m.LiefNr) || fmtVal(m.KommNr) || fmtVal(m.BorderoNr)
                            || fmtVal(m.AufKlasse) || fmtVal(m.LiefTxt) || fmtVal(m.VerkArt)) && (
                            <div style={S.row}>
                                {fmtVal(m.LiefNr)    && <><Lbl t="Liefer-Nr.:" /><div style={inp()}>{fmtVal(m.LiefNr)}</div></>}
                                {fmtVal(m.KommNr)    && <><Lbl t="Komm-Nr.:" /><div style={inp()}>{fmtVal(m.KommNr)}</div></>}
                                {fmtVal(m.RefNr)     && <><Lbl t="Referenz:" /><div style={inp()}>{fmtVal(m.RefNr)}</div></>}
                                {fmtVal(m.BorderoNr) && <><Lbl t="Bordereau:" /><div style={inp()}>{fmtVal(m.BorderoNr)}</div></>}
                                {fmtVal(m.AufKlasse) && <><Lbl t="Klasse:" /><div style={inp()}>{fmtVal(m.AufKlasse)}</div></>}
                                {fmtVal(m.LiefTxt)   && <><Lbl t="Terminvermerk:" /><div style={inp()}>{fmtVal(m.LiefTxt)}</div></>}
                                {fmtVal(m.VerkArt)   && <><Lbl t="Verk.-Art:" /><div style={inp()}>{fmtVal(m.VerkArt)}</div></>}
                            </div>
                        )}

                        {/* Positions */}
                        {positList.filter(p => fmtVal(p.map?.AufNr ?? p.map?.TransNr) === aufNr || auftrList.length === 1)
                            .length > 0 && (
                            <>
                                <div style={S.sectionTitle}>Positionen</div>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ borderCollapse: "collapse", fontSize: 11, background: "#fff", width: "100%" }}>
                                        <thead>
                                            <tr>
                                                {["Pos.", "VPE", "Pal", "LM", "Stellplätze", "Inhalt", "Artikel-Nr.",
                                                  "TatsGew.", "FpflGew.", "Zeichen", "Länge", "Breite", "Höhe"].map(h => (
                                                    <th key={h} style={S.th}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {positList.filter(p =>
                                                fmtVal(p.map?.AufNr ?? p.map?.TransNr) === aufNr || auftrList.length === 1
                                            ).map((p, i) => {
                                                const pm = p.map;
                                                const matchingLademi = lademiList.filter(ldm =>
                                                    fmtVal(ldm.map?.AufNr) === fmtVal(pm.AufNr) &&
                                                    fmtVal(ldm.map?.AufPosNr) === fmtVal(pm.AufPosNr)
                                                );
                                                const { vpe, pal } = formatLademittel(matchingLademi);
                                                return (
                                                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                                                        <td style={S.td}>{fmtVal(pm.AufPosNr)}</td>
                                                        <td style={S.td}>{vpe || fmtVal(pm.VPE ?? pm.PackCode)}</td>
                                                        <td style={S.td}>{pal || fmtVal(pm.Stellplaetze ?? pm.PAL)}</td>
                                                        <td style={S.td}>{fmtVal(pm.LMAnz)}</td>
                                                        <td style={S.td}>{fmtVal(pm.SPAnz)}</td>
                                                        <td style={S.tdWrap}>{fmtVal(pm.Inhalt)}</td>
                                                        <td style={S.td}>{fmtVal(pm.Artikel ?? pm.EAN)}</td>
                                                        <td style={S.td}>{fmtVal(pm.TatsGew)}</td>
                                                        <td style={S.td}>{fmtVal(pm.FpflGew)}</td>
                                                        <td style={S.td}>{fmtVal(pm.Zeichen)}</td>
                                                        <td style={S.td}>{fmtVal(pm.Laenge)}</td>
                                                        <td style={S.td}>{fmtVal(pm.Breite)}</td>
                                                        <td style={S.td}>{fmtVal(pm.Hoehe)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {/* Texte (collapsible) */}
                        {textList.length > 0 && (
                            <>
                                <div onClick={() => setTextsOpen(o => !o)}
                                    style={{ ...S.divider, cursor: "pointer", userSelect: "none",
                                        display: "flex", alignItems: "center", gap: 4 }}>
                                    <span style={{ fontSize: 10, color: "#555", background: "#e8e8e8",
                                        padding: "0 4px", marginTop: -7, position: "relative" }}>
                                        {textsOpen ? "▾" : "▸"} Texte ({textList.length})
                                    </span>
                                    <TextArtenPopup/>
                                </div>
                                {textsOpen && (
                                    <div style={{ background: "#fff", border: "1px solid #ccc", padding: 4 }}>
                                        {textList.filter(t =>
                                            fmtVal(t.map?.AufNr ?? "") === aufNr ||
                                            fmtVal(t.map?.AufNr ?? "") === "" ||
                                            auftrList.length === 1
                                        ).map((t, i) => {
                                            const art  = t.map?.TxtArtNr;
                                            const code = fmtVal(art);
                                            const label = textLabel(art);
                                            const text = fmtVal(t.map?.TxtStr1 ?? t.map?.Txt ?? t.map?.Text);
                                            return (
                                                <div key={i} style={{ marginBottom: 2 }}>
                                                    <span style={{ ...S.lblBold, color: teal }}>
                                                        {label ? `${label} (${code})` : `Text ${code}`}: 
                                                    </span>
                                                    <span style={{ fontSize: 11 }}>{text}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {/* NVE (collapsible) */}
                        {nveList.length > 0 && (
                            <>
                                <div onClick={() => setNveOpen(o => !o)}
                                    style={{ ...S.divider, cursor: "pointer", userSelect: "none",
                                        display: "flex", alignItems: "center", gap: 4 }}>
                                    <span style={{ fontSize: 10, color: "#555", background: "#e8e8e8",
                                        padding: "0 4px", marginTop: -7, position: "relative" }}>
                                        {nveOpen ? "▾" : "▸"} NVE / Ladeeinheiten ({nveList.length})
                                    </span>
                                </div>
                                {nveOpen && (
                                    <table style={{ borderCollapse: "collapse", fontSize: 11, background: "#fff" }}>
                                        <thead>
                                            <tr>
                                                {["Nr.", "Pos.", "NVE-Code"].map(h => (
                                                    <th key={h} style={S.th}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {nveList.map((n, i) => (
                                                <tr key={i}>
                                                    <td style={S.td}>{fmtVal(n.map?.LfdNr ?? i + 1)}</td>
                                                    <td style={S.td}>{fmtVal(n.map?.PosNr)}</td>
                                                    <td style={S.td}>{fmtVal(n.map?.NVE)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </>
                        )}
                    </div>
                );
            })}
        </>
    );
}

export function WinSpedPreviewViewer({ output }: WinSpedPreviewViewerProps) {
    const [records, setRecords]     = React.useState<any[] | null>(null);
    const [loading, setLoading]     = React.useState(false);
    const [error,   setError]       = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!output) return;
        setLoading(true);
        setError(null);
        setRecords(null);

        fetch("/converter/lisDocToRecordsWithDescription?mode=WinSped", {
            method: "POST",
            headers: {
                "Content-Type": "application/json; charset=utf-8",
                "x-request-id": uuid(),
                "encoding": "ISO-8859-1",
            },
            body: output,
        })
            .then(r => r.json())
            .then(data => { setRecords(data); setLoading(false); })
            .catch(e  => { setError(String(e)); setLoading(false); });
    }, [output]);

    if (loading) {
        return <div style={{ padding: 16, fontSize: 12, fontFamily: "Segoe UI, Tahoma, sans-serif" }}>
            Lade WinSped-Daten…
        </div>;
    }
    if (error) {
        return <div style={{ padding: 16, fontSize: 12, color: "#c00", fontFamily: "Segoe UI, Tahoma, sans-serif" }}>
            Fehler beim Parsen: {error}
        </div>;
    }
    if (!records || records.length === 0) {
        return <div style={{ padding: 16, fontSize: 12, color: "#888", fontStyle: "italic",
            fontFamily: "Segoe UI, Tahoma, sans-serif" }}>
            Kein WinSped-Dokument erkannt. Erwartet wird ein LIS-Format (START|...).
        </div>;
    }

    const start = records.find(r => r.map?.Satzart === "START");
    const ende  = records.find(r => r.map?.Satzart === "ENDE");

    return (
        <div style={S.root}>
            {/* Dokument-Header */}
            {start && (
                <div style={{ fontSize: 10, color: "#555", marginBottom: 4, display: "flex", gap: 10 }}>
                    <span>Referenz: {fmtVal(start.map?.Referenz)}</span>
                    {fmtVal(start.map?.EigName1) && <span>Von: {fmtVal(start.map?.EigName1)}</span>}
                    {fmtVal(start.map?.ExpName1) && <span>An: {fmtVal(start.map?.ExpName1)}</span>}
                    {fmtVal(start.map?.Erstellt) && <span>Erstellt: {fmtDateObj(start.map.Erstellt)}</span>}
                    {ende && <span>Aufträge: {fmtVal(ende.map?.AnzAuftr ?? "")}</span>}
                </div>
            )}
            <OrderView records={records} />
        </div>
    );
}
